import { csvText } from "./csv.ts";
import { clientFilters, type ClientCounts, type ClientFilter, type ClientRow } from "./schemas/clients.ts";
import { localDate, shiftDateKey } from "./time.ts";

/**
 * The rules of the Clients table (Pilates studio shape, C1). Pure, so the API, Desk web and the
 * mobile app read one set of words and one meaning for each filter.
 */

/** A pack that ends in this many days or fewer is "ending soon". */
export const ENDING_SOON_DAYS = 14;
/** Or this many sessions left or fewer. */
export const ENDING_SOON_SESSIONS = 2;
/** A client who came before and has no visit in this many days, and no next booking, is "quiet". */
export const QUIET_DAYS = 21;

/** The words on the filter chips. */
export const clientFilterLabels: Record<ClientFilter, string> = {
  all: "All",
  active: "Active",
  ending_soon: "Ending soon",
  intro: "Intro offer",
  quiet: "Quiet",
};

type Facts = Pick<ClientRow, "sessionsLeft" | "endsOn" | "lastVisitAt" | "nextBookingAt" | "intro">;

/**
 * Which rows a filter keeps. `today` is today in the business timezone (`yyyy-MM-dd`); the last
 * visit is read as a day in the same timezone, so a visit late in the evening counts on its own day.
 *
 * - active: the client holds a current pack with sessions left, or has a next booking.
 * - ending soon: a current pack, and it ends in 14 days or fewer, or 2 sessions or fewer are left.
 * - intro: a current pack is an intro offer.
 * - quiet: the client came before, the last visit was 21 or more days ago, and nothing is booked.
 */
export function clientFilterTest(filter: ClientFilter, today: string, timeZone: string): (row: Facts) => boolean {
  switch (filter) {
    case "active":
      return (row) => (row.sessionsLeft ?? 0) > 0 || row.nextBookingAt !== null;
    case "ending_soon":
      return (row) =>
        row.sessionsLeft !== null &&
        row.sessionsLeft > 0 &&
        (row.sessionsLeft <= ENDING_SOON_SESSIONS || (row.endsOn !== null && row.endsOn <= shiftDateKey(today, ENDING_SOON_DAYS)));
    case "intro":
      return (row) => row.intro;
    case "quiet":
      return (row) =>
        row.nextBookingAt === null &&
        row.lastVisitAt !== null &&
        localDate(new Date(row.lastVisitAt), timeZone) <= shiftDateKey(today, -QUIET_DAYS);
    default:
      return () => true;
  }
}

/** How many rows each filter keeps. */
export function clientCounts(rows: readonly Facts[], today: string, timeZone: string): ClientCounts {
  return Object.fromEntries(
    clientFilters.map((filter) => [filter, rows.filter(clientFilterTest(filter, today, timeZone)).length]),
  ) as ClientCounts;
}

/** The flags of a row as short words, in one order: "Intro offer", "Paused", "Owes". */
export function clientFlagWords(row: Pick<ClientRow, "intro" | "paused" | "owed">): string[] {
  return [row.intro ? "Intro offer" : null, row.paused ? "Paused" : null, row.owed ? "Owes" : null].filter(
    (word): word is string => word !== null,
  );
}

/**
 * The export of the Clients table: the columns of the table, one line per client. `memberWord` is
 * the business's word for the person ("Client"). The formula guard is in `csvText`.
 *
 * Health answers (C2) never go in this file: they are on the client page only. Keep them out here.
 */
export function clientsCsv(rows: readonly ClientRow[], timeZone: string, memberWord: string): string {
  const header = [
    memberWord ? memberWord[0]!.toUpperCase() + memberWord.slice(1) : "Name",
    "Mobile number",
    "Package",
    "Sessions left",
    "Ends on",
    "Last visit",
    "Next booking",
    "Flags",
  ];
  const lines = rows.map((row) => [
    row.name,
    row.phoneE164 ?? "",
    row.packageName ?? "",
    row.sessionsLeft ?? "",
    row.endsOn ?? "",
    row.lastVisitAt ? localDate(new Date(row.lastVisitAt), timeZone) : "",
    row.nextBookingAt ? localDate(new Date(row.nextBookingAt), timeZone) : "",
    clientFlagWords(row).join("; "),
  ]);
  return csvText(header, lines);
}
